import React, {createContext, useContext, useMemo, useReducer} from 'react';
import {ActionType} from 'typesafe-actions';
import {ThemeName} from '@taopix/taopix-design-system';
import {AssetServiceActionTypes} from './AssetServicesActions';
import {AssetMetaInterface} from '@taopix/taopix-core/dist/types/types';

type RootAction = ActionType<typeof import('./AssetServicesActions')>;

export type AssetServiceState = {
  open: boolean;
  anchor: string;
  useParentWidth: boolean;
  assets: AssetMetaInterface[];
  theme?: ThemeName | string;
  themeTip?: string;
  selectedServiceID: number;
  serviceDialogOpen: boolean;
};

export type AssetServiceContextState = {
  state: AssetServiceState;
  dispatch: React.Dispatch<RootAction>;
};

export const AssetServiceContext = createContext<AssetServiceContextState>(
  {} as AssetServiceContextState
);

const AssetServicesReducer = (state: AssetServiceState, action: RootAction): AssetServiceState => {
  switch (action.type) {
    case AssetServiceActionTypes.SET_OPEN_STATE: {
      return {
        ...state,
        ...action.payload,
      };
    }
    case AssetServiceActionTypes.SET_SELECTED_SERVICE: {
      return {
        ...state,
        serviceDialogOpen: true,
        selectedServiceID: action.payload,
      };
    }
    case AssetServiceActionTypes.RESET_SELECTED_SERVICE: {
      return {
        ...state,
        serviceDialogOpen: false,
        selectedServiceID: 0,
      };
    }
    case AssetServiceActionTypes.SET_SERVICE_DIALOG_OPEN_STATE: {
      return {
        ...state,
        serviceDialogOpen: action.payload,
      };
    }
    case AssetServiceActionTypes.SET_SELECTED_ASSETS: {
      return {
        ...state,
        assets: [...action.payload],
      };
    }
    case AssetServiceActionTypes.RESET_SELECTED_ASSETS: {
      return {
        ...state,
        assets: [],
      };
    }
    default:
      return state;
  }
};

export const AssetServicesProvider = ({children}: {children: any}) => {
  const [state, dispatch] = useReducer(AssetServicesReducer, {
    open: false,
    useParentWidth: false,
    anchor: '',
    assets: [],
    theme: '',
    themeTip: '',
    selectedServiceID: 0,
    serviceDialogOpen: false,
  });

  const contextValue = useMemo(
    () => ({
      state,
      dispatch,
    }),
    [state, dispatch]
  );

  return (
    <AssetServiceContext.Provider value={contextValue}>{children}</AssetServiceContext.Provider>
  );
};

export const useAssetServices = () => {
  const context = useContext(AssetServiceContext);
  if (context === undefined) {
    throw new Error('useAssetServices must be used within an AssetServicesProvider');
  }
  return context;
};
